import React, { useCallback, useEffect, useState } from 'react';
import { apiService, apiBaseUrl } from '../../services/api';

const date = (value) => value ? new Date(value * 1000).toLocaleString('fr-FR') : 'Jamais';
// Connecteur MCP distant (Streamable HTTP) : la même clé, les mêmes droits et le même journal que l'API.
const mcpUrl = `${apiBaseUrl.replace(/\/$/, '')}/agent-api/v1/mcp`;
const claudeCommand = (token) => `claude mcp add --transport http deepflow ${mcpUrl} --header "Authorization: Bearer ${token}"`;

export default function AgentAccess() {
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [selected, setSelected] = useState(['data:read', 'drive:read', 'knowledge:read']);
  const [name, setName] = useState('Mon agent');
  const [days, setDays] = useState(30);
  const [created, setCreated] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState('');
  const refresh = useCallback(async () => {
    const [keyData, scopeData, auditData] = await Promise.all([
      apiService.get('/agent-access/keys'), apiService.get('/agent-access/scopes'), apiService.get('/agent-access/audit'),
    ]);
    setKeys(keyData.keys); setScopes(scopeData.scopes); setAudit(auditData.items); setLoaded(true);
  }, []);
  useEffect(() => { refresh().catch((err) => setError(err.message)); }, [refresh]);

  const create = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const key = await apiService.post('/agent-access/keys', { name, scopes: selected, expires_in_days: Number(days) });
      setCreated({ ...key, format: 'deepflow-agent-connection-v1', api_url: apiBaseUrl.replace(/\/$/, '') });
      await refresh();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    setBusy(true); setError('');
    try {
      await apiService.delete(`/agent-access/keys/${id}`);
      if (created?.id === id) setCreated(null);
      await refresh();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  // La clé n'est jamais affichée : seulement copiée (presse-papiers) ou enregistrée (fichier du pont Python).
  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
    } catch (err) { setError('Copie impossible : ' + (err?.message || 'presse-papiers indisponible')); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(created, null, 2)], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = `deepflow-connection-${created.id}.json`; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return <main className="max-w-5xl mx-auto p-6 space-y-6">
    <div><h1 className="text-2xl font-semibold">Accès agents &amp; API</h1>
      <p className="text-gray-600 mt-2">Connectez votre agent aux projets, documents et outils de Deepflow avec une clé personnelle, révocable et limitée aux droits choisis.</p>
    </div>
    {error && <p role="alert" className="bg-red-50 text-red-800 rounded p-3">{error}</p>}
    {!loaded && !error && <p>Chargement des accès…</p>}
    <form onSubmit={create} className="bg-white border rounded-xl p-5 space-y-4">
      <h2 className="font-semibold text-lg">Créer une connexion</h2>
      <div className="flex gap-4 flex-wrap">
        <label className="flex flex-col gap-1">Nom de l’agent<input required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} className="border rounded p-2" /></label>
        <label className="flex flex-col gap-1">Validité<select value={days} onChange={(e) => setDays(e.target.value)} className="border rounded p-2">
          {[7, 30, 90, 365].map((n) => <option key={n} value={n}>{n} jours</option>)}
        </select></label>
      </div>
      <fieldset className="space-y-2"><legend className="font-medium mb-2">Droits de la clé</legend>
        {scopes.map((scope) => <label key={scope.name} className="flex gap-2 items-start">
          <input type="checkbox" className="mt-1" checked={selected.includes(scope.name)} onChange={(e) => setSelected((old) => e.target.checked ? [...old, scope.name] : old.filter((s) => s !== scope.name))} />
          <span>{scope.description}</span>
        </label>)}
      </fieldset>
      <p className="text-sm text-gray-600">La lecture couvre tous les projets Deepflow et l’index Knowledge partagé. Drive utilise votre compte Google connecté et ses autorisations. Les modifications sont attribuées à votre compte.</p>
      <button disabled={busy || !loaded || !selected.length || !!created} className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50">{busy ? 'En cours…' : 'Créer la clé'}</button>
    </form>
    {created && <section className="border border-blue-300 bg-blue-50 rounded-xl p-5 space-y-4">
      <h2 className="font-semibold">Connexion créée</h2>
      <p>Copiez ou enregistrez la clé maintenant : elle ne sera plus récupérable après fermeture de ce panneau.</p>
      <div className="space-y-2">
        <h3 className="font-medium">Connecteur MCP distant</h3>
        <p className="text-sm">Une adresse et la clé suffisent, sans rien installer : <code className="break-all">{mcpUrl}</code></p>
        <p className="text-sm">Claude Code : <code className="break-all">{claudeCommand('df_agent_…')}</code></p>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => copy('command', claudeCommand(created.token))} className="bg-blue-700 text-white rounded px-4 py-2">Copier la commande Claude Code</button>
          <button onClick={() => copy('url', mcpUrl)} className="border rounded px-4 py-2">Copier l’adresse</button>
          <button onClick={() => copy('token', created.token)} className="border rounded px-4 py-2">Copier la clé</button>
        </div>
        {copied && <p role="status" className="text-sm text-green-800">{{ command: 'Commande copiée.', url: 'Adresse copiée.', token: 'Clé copiée.' }[copied]}</p>}
      </div>
      <div className="space-y-2">
        <h3 className="font-medium">Pont Python local</h3>
        <p className="text-sm">Pour le connecteur integrations/deepflow_mcp (fichiers locaux, profils Hermes) : un fichier privé qui contient la clé.</p>
        <button onClick={download} className="border rounded px-4 py-2">Télécharger la connexion</button>
      </div>
      <button onClick={() => { setCreated(null); setCopied(''); }} className="border rounded px-4 py-2">J’ai conservé la clé</button>
    </section>}
    <section className="bg-white border rounded-xl p-5 space-y-3">
      <h2 className="font-semibold text-lg">Mes clés</h2>
      {loaded && keys.length === 0 && <p>Aucune clé créée.</p>}
      {keys.map((key) => <div key={key.id} className="border-t py-3 flex justify-between gap-4">
        <div><p className="font-medium">{key.name} {key.revoked_at ? '· Révoquée' : key.expires_at * 1000 < Date.now() ? '· Expirée' : '· Active'}
          {key.kind === 'oauth' && <span className="ml-2 text-xs bg-indigo-100 text-indigo-800 rounded px-2 py-0.5">Connecteur OAuth</span>}</p>
          <p className="text-sm text-gray-600">Expiration : {date(key.expires_at)} · Dernier accès : {date(key.last_used_at)}</p>
          <p className="text-xs text-gray-500">{key.scopes.join(' · ')}</p></div>
        {!key.revoked_at && <button disabled={busy} onClick={() => revoke(key.id)} className="text-red-700 self-start border rounded px-3 py-1">Révoquer</button>}
      </div>)}
    </section>
    <section className="bg-white border rounded-xl p-5 space-y-3">
      <div className="flex justify-between"><h2 className="font-semibold text-lg">Derniers appels</h2>
        <button onClick={() => refresh().catch((err) => setError(err.message))} className="text-blue-700">Actualiser</button></div>
      <p className="text-sm text-gray-600">Les 50 derniers appels. Les arguments et le contenu des documents ne sont pas enregistrés dans ce journal. Un appel en cours ou interrompu peut avoir produit une modification : vérifiez les données avant de le relancer.</p>
      {audit.length === 0 ? <p>Aucun appel enregistré.</p> : <div className="overflow-x-auto"><table className="w-full text-sm text-left">
        <thead><tr><th className="p-2">Date</th><th className="p-2">Clé / outil</th><th className="p-2">État</th><th className="p-2">Identifiant de requête</th></tr></thead>
        <tbody>{audit.map((call) => <tr key={call.id} className="border-t"><td className="p-2">{date(call.started_at)}</td><td className="p-2">{call.key_name}<br />{call.tool_name}</td><td className="p-2">{call.status}</td><td className="p-2 break-all">{call.request_id || '—'}</td></tr>)}</tbody>
      </table></div>}
    </section>
    <section className="text-sm text-gray-600 space-y-2">
      <h2 className="font-semibold text-gray-900">Pour connecter un agent</h2>
      <p>Connecteur MCP distant (Streamable HTTP) : <code className="break-all">{mcpUrl}</code> avec votre clé en Authorization: Bearer. Claude Code : <code className="break-all">claude mcp add --transport http deepflow {mcpUrl} --header "Authorization: Bearer &lt;clé&gt;"</code>. Claude Desktop et les clients sans HTTP : <code className="break-all">npx mcp-remote {mcpUrl} --header "Authorization: Bearer &lt;clé&gt;"</code>. Mêmes droits, mêmes limites et même journal que l’API ; chaque modification porte un _request_id fourni par l’agent.</p>
      <p>claude.ai (web et mobile) : Paramètres → Connecteurs → Ajouter un connecteur personnalisé, avec l’adresse <code className="break-all">{mcpUrl}</code> seule. Claude ouvre ensuite Deepflow pour que vous choisissiez les droits et la durée ; la connexion apparaît ici comme « Connecteur OAuth ».</p>
      <p>Pont Python local : integrations/deepflow_mcp du dépôt Deepflow. Il charge le fichier de connexion téléchargé et publie les outils autorisés en MCP (STDIO).</p>
      <p>Accès HTTP direct : <code className="break-all">{apiBaseUrl}/agent-api/v1/discovery</code> avec votre clé en Authorization: Bearer. Le catalogue et la documentation OpenAPI sont disponibles sous /tools et /openapi.json.</p>
    </section>
  </main>;
}
